import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface UserProfile {
  id: string;
  username: string;
  display_name: string;
  is_admin: boolean;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  isAdmin: boolean;
  signUp: (email: string, password: string, username: string, displayName: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = profile?.is_admin || false;

  const fetchUserProfile = async (userId: string): Promise<void> => {
    console.log('Fetching profile for user:', userId);
    
    // Check localStorage cache first
    try {
      const cachedProfile = localStorage.getItem(`profile_${userId}`);
      if (cachedProfile) {
        const parsed = JSON.parse(cachedProfile);
        console.log('Using cached profile:', parsed);
        setProfile(parsed);
        // Still fetch from database in background to update cache
      }
    } catch (error) {
      console.log('Cache read error:', error);
    }
    
    try {
      // Fetch from database with shorter timeout
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error || !data) {
        console.log('Profile not found in database:', error?.message || 'No data');
        
        // Create fallback profile
        const fallbackProfile = {
          id: userId,
          username: '',
          display_name: '',
          is_admin: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        setProfile(fallbackProfile);
        localStorage.setItem(`profile_${userId}`, JSON.stringify(fallbackProfile));
      } else {
        console.log('Profile fetched from database:', data);
        setProfile(data);
        // Cache the profile
        localStorage.setItem(`profile_${userId}`, JSON.stringify(data));
      }
      
    } catch (error) {
      console.error('Profile fetch error:', error);
      
      // Only set fallback if we don't have cached data
      if (!profile) {
        const fallbackProfile = {
          id: userId,
          username: '',
          display_name: '',
          is_admin: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        setProfile(fallbackProfile);
      }
    }
  };

  useEffect(() => {
    console.log('Auth context initializing...');

    const initAuth = async () => {
      try {
        // Get session - this should be instant from localStorage
        const { data: { session } } = await supabase.auth.getSession();
        
        console.log('Session check complete:', session ? 'User exists' : 'No user');
        
        setSession(session);
        setUser(session?.user ?? null);
        
        // Set loading false immediately after getting session
        setLoading(false);
        
        // Fetch profile in background (non-blocking)
        if (session?.user) {
          console.log('Fetching user profile in background...');
          fetchUserProfile(session.user.id).catch(err => {
            console.error('Background profile fetch failed:', err);
          });
        } else {
          setProfile(null);
        }
        
      } catch (error) {
        console.error('Auth init error:', error);
        setLoading(false);
      }
    };

    initAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state change:', event);
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        // Fetch profile in background for auth changes too
        fetchUserProfile(session.user.id).catch(err => {
          console.error('Auth change profile fetch failed:', err);
        });
      } else {
        setProfile(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signUp = async (email: string, password: string, username: string, displayName: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          username,
          display_name: displayName,
        },
      },
    });

    if (error) {
      throw error;
    }
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      throw error;
    }
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      throw error;
    }
  };

  const value = {
    user,
    session,
    profile,
    isAdmin,
    signUp,
    signIn,
    signOut,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};